import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, X, Trash2 } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import { useBasket } from "@/context/BasketContext";
import { priceForInterval, formatIntervalPrice, isFreeProduct, symbolFor, INTERVAL_LABELS } from "@/utils/priceIntervals";

const API = getApiUrl();

/**
 * Floating basket button — fixed to the bottom-right of every page (kept
 * clear of CookieConsent, which occupies bottom-left). Click opens a
 * slide-in drawer with the current basket contents and a "Checkout"
 * shortcut. Hidden entirely when the basket is empty (no visual clutter).
 * Shopping flows funnel through the full `/basket` page for line-level editing.
 */
export default function BasketDrawer() {
  const basket = useBasket();
  const { drawerOpen: open, openDrawer, closeDrawer } = basket;
  useLockBodyScroll(open);
  const [products, setProducts] = useState({});   // id → product doc
  const navigate = useNavigate();
  const location = useLocation();
  // Agents/admins have their own dedicated buy-products UI inside the
  // dashboards — the floating storefront basket is clutter there.
  const isAdminRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/property-admin");

  // Load product details for whatever's in the basket so we can show
  // name/price/image without duplicating them in localStorage.
  useEffect(() => {
    if (basket.items.length === 0) return;
    axios.get(`${API}/products`).then(({ data }) => {
      const byId = {};
      (data.products || []).forEach((p) => { byId[p.product_id] = p; });
      setProducts(byId);
    }).catch(() => {});
  }, [basket.items]);

  if (basket.count === 0 || isAdminRoute) return null;

  const lines = basket.items
    .map((i) => ({ ...i, product: products[i.product_id] }))
    .filter((l) => l.product);
  const subtotal = lines.reduce((s, l) => s + priceForInterval(l.product, l.billing_interval), 0);
  const currency = lines[0]?.product?.currency || "GBP";
  const symbol = symbolFor(currency);
  // Derived from live product pricing (not a stored flag) so a free item
  // (e.g. an auto-seeded freebie) never masks the real subscription/one-off
  // type the shopper is actually paying for.
  const paidInterval = (lines.find((l) => !isFreeProduct(l.product)) || lines[0])?.billing_interval;
  const intervalSuffix = paidInterval === "monthly" ? "/mo" : paidInterval === "annual" ? "/yr" : "";
  const priceLabel = lines.length
    ? `${symbol}${subtotal.toFixed(2)}${intervalSuffix}`
    : `${basket.count} item${basket.count === 1 ? "" : "s"}`;

  return (
    <>
      <button
        type="button"
        onClick={openDrawer}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60] bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-2 pl-4 pr-3 h-14"
        data-testid="basket-button"
        aria-label={`Open basket · ${basket.count} item${basket.count === 1 ? "" : "s"} · ${priceLabel}`}
      >
        <span className="relative flex items-center">
          <ShoppingCart className="h-5 w-5" />
          <span
            className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center"
            data-testid="basket-count"
          >
            {basket.count}
          </span>
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[10px] uppercase tracking-wide opacity-80">Basket</span>
          <span className="text-sm font-semibold whitespace-nowrap" data-testid="basket-subtotal">{priceLabel}</span>
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex" role="dialog" aria-modal="true">
          <button
            type="button"
            className="flex-1 bg-black/40"
            onClick={closeDrawer}
            aria-label="Close basket"
          />
          <aside className="w-full max-w-sm bg-background h-full shadow-2xl flex flex-col" data-testid="basket-drawer">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-semibold">Your Basket ({basket.count})</h3>
              <div className="flex items-center gap-1">
                {basket.count > 0 && (
                  <button
                    type="button"
                    onClick={() => basket.clear()}
                    className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-red-600 px-2 py-1 rounded hover:bg-red-50"
                    data-testid="basket-clear-all"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Clear basket
                  </button>
                )}
                <button type="button" onClick={closeDrawer} aria-label="Close" className="p-1 hover:bg-muted rounded">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {basket.count === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8" data-testid="drawer-empty-state">
                  Your basket is empty.
                </p>
              ) : lines.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Loading…</p>
              ) : lines.map((l) => (
                <div key={l.product_id} className="flex gap-3 border-b pb-3">
                  {l.product.image_url
                    ? <img src={l.product.image_url} alt={l.product.name} className="h-14 w-14 object-cover rounded" />
                    : <div className="h-14 w-14 bg-muted rounded" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{l.product.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatIntervalPrice(l.product, l.billing_interval, currency)}
                      {l.billing_interval && l.billing_interval !== "one_off" && (
                        <span className="ml-1 text-[10px] uppercase tracking-wide text-primary/70">
                          {INTERVAL_LABELS[l.billing_interval]}
                        </span>
                      )}
                    </div>
                  </div>
                  <button type="button" onClick={() => basket.remove(l.product_id)} className="text-muted-foreground hover:text-red-600 text-xs">Remove</button>
                </div>
              ))}
            </div>

            <div className="border-t p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-serif text-lg">{symbol}{subtotal.toFixed(2)}{intervalSuffix}</span>
              </div>
              <button
                type="button"
                onClick={() => { closeDrawer(); navigate("/checkout"); }}
                className="w-full text-sm bg-primary text-primary-foreground rounded py-2 transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40"
                data-testid="drawer-checkout"
                disabled={basket.count === 0}
                aria-disabled={basket.count === 0}
                title={basket.count === 0 ? "Add an item to your basket to continue to checkout" : undefined}
              >
                Checkout
              </button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
